import * as React from 'react';

import {
  DocumentType,
  enumDocumentType,
} from '@rematter/frontend-lib-api-client';
import { Checkbox, ContentCard } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import startCase from 'lodash/startCase';

import { useTranslations } from '../../i18n/useTranslations';

import { PrintingActionButtons } from '~/modules/trade/_components/_shared/FloatingActionBar';
import { useGenerateDocumentsAndSendEmail } from '~/modules/trade/hooks/useGenerateDocumentsAndSendEmail';
import { usePrintWithBrowser } from '~/modules/trade/hooks/usePrintWithBrowser';
import { LoadRecord } from '~/modules/trade/types/load';

export const LOAD_DOCUMENT_TYPES: DocumentType[] = [
  enumDocumentType.Invoice,
  enumDocumentType.PackingList,
  enumDocumentType.BillOfLading,
  // enumDocumentType.TicketReceipt,
];

// Document selection for a single load
export type LoadDocumentSelection = Partial<Record<DocumentType, boolean>>;

interface LoadTabDocumentsProps {
  load: LoadRecord;
}

export const LoadTabDocuments: React.FC<LoadTabDocumentsProps> = ({ load }) => {
  const t = useTranslations();
  const hasInvoice = load.invoiceId != null;

  // Hooks for printing and emailing
  const { print, isPrinting } = usePrintWithBrowser();
  const { sendDocumentsAndEmail, isPending } =
    useGenerateDocumentsAndSendEmail();

  // Document selection state - simplified for single load
  const [selectedDocuments, setSelectedDocuments] =
    React.useState<LoadDocumentSelection>({
      Invoice: false,
      PackingList: false,
      BillOfLading: false,
      SalesOrder: false,
      PurchaseOrder: false,
      ShippingSummary: false,
      BookingInvoice: false,
      LoadInvoice: false,
    });

  const [isDownloading, setIsDownloading] = React.useState(false);

  // Count selected documents
  const selectedCount = Object.values(selectedDocuments).filter(Boolean).length;

  const handleDocumentSelect = React.useCallback(
    (documentType: DocumentType, checked: boolean) => {
      setSelectedDocuments((prev) => ({
        ...prev,
        [documentType]: checked,
      }));
    },
    [],
  );

  const handlePrintDocuments = React.useCallback(async () => {
    await print({
      documents: Object.entries(selectedDocuments)
        .filter(([_, checked]) => checked)
        .map(([documentType]) => ({
          documentType: documentType as DocumentType,
          loadId: load.id,
          invoiceId:
            documentType === 'Invoice'
              ? (load.invoiceId ?? undefined)
              : undefined,
        })),
    });
  }, [selectedDocuments, print, load.id, load.invoiceId]);

  const handleDownloadDocuments = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      await print({
        documents: Object.entries(selectedDocuments)
          .filter(([_, checked]) => checked)
          .map(([documentType]) => ({
            documentType: documentType as DocumentType,
            loadId: load.id,
            invoiceId:
              documentType === 'Invoice'
                ? (load.invoiceId ?? undefined)
                : undefined,
          })),
        action: 'download',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [selectedDocuments, print, load.id, load.invoiceId]);

  const handleSendEmail = React.useCallback(
    async (emails: string[]) => {
      if (emails.length === 0) {
        return;
      }

      const selectedDocumentTypes = Object.entries(selectedDocuments)
        .filter(([_, checked]) => checked)
        .map(([documentType]) => documentType as DocumentType);

      if (selectedDocumentTypes.length === 0) {
        return;
      }

      try {
        await sendDocumentsAndEmail({
          documents: selectedDocumentTypes.map((documentType) => ({
            documentType,
            loadId: load.id,
          })),
          email: {
            to: emails,
            subject: t.loadDetail.documents.email.subject(load.loadNumber),
            textBody: t.loadDetail.documents.email.textBody({
              loadNumber: load.loadNumber,
              documentTypes: selectedDocumentTypes.join(', '),
            }),
            htmlBody: t.loadDetail.documents.email.htmlBody({
              loadNumber: load.loadNumber,
              documentTypes: selectedDocumentTypes.join(', '),
            }),
          },
        });

        // Clear selection after successful email send
        setSelectedDocuments({
          Invoice: false,
          PackingList: false,
          BillOfLading: false,
          SalesOrder: false,
          PurchaseOrder: false,
          ShippingSummary: false,
          BookingInvoice: false,
          LoadInvoice: false,
        });
      } catch (error) {
        // Error handling is done in the hook
        console.error('Failed to send documents via email:', error);
      }
    },
    [selectedDocuments, sendDocumentsAndEmail, load.id, load.loadNumber, t],
  );

  const handlePostPrint = React.useCallback(() => {
    setSelectedDocuments({
      Invoice: false,
      PackingList: false,
      BillOfLading: false,
      SalesOrder: false,
      PurchaseOrder: false,
      WeightTicket: false,
      OutboundScaleTicket: false,
      ShippingSummary: false,
      BookingInvoice: false,
      LoadInvoice: false,
    });
  }, []);

  const isDocumentAvailable = (documentType: DocumentType) => {
    if (documentType === 'Invoice') {
      return hasInvoice;
    }
    return true;
  };

  const getDocumentDisplayName = (documentType: DocumentType) => {
    const key = documentType.toLowerCase() as keyof typeof t;
    return (
      t.loadDetail.documents.types[key as never] || startCase(documentType)
    );
  };

  return (
    <div className="@LoadTabDocuments flex h-full flex-col p-5">
      <ContentCard className="flex h-full flex-1 flex-col pb-0">
        <div className="flex flex-1 flex-col items-center justify-start py-5 text-center">
          {/* Header */}
          <div className="mb-6">
            <h2 className="mb-2 text-lg font-semibold text-neutral">
              {t.loadDetail.documents.title}
            </h2>
            <p className="text-sm text-neutral/80">
              {t.loadDetail.documents.subtitle}
            </p>
          </div>

          {/* Document list */}
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            {LOAD_DOCUMENT_TYPES.map((documentType) => {
              const isAvailable = isDocumentAvailable(documentType);
              const isSelected = selectedDocuments[documentType];
              const displayName = getDocumentDisplayName(documentType);

              return (
                <div
                  key={documentType}
                  className={cn(
                    'flex h-[60px] w-[380px] cursor-pointer items-center gap-3 rounded-md border p-4 transition-colors',
                    'border-neutral/10 outline-2 outline-transparent hover:border-neutral/40',
                    isSelected &&
                      '!border-transparent bg-primary/5 shadow-[0_0_0_2px_var(--color-primary/20)] outline-primary',
                    !isAvailable && 'cursor-not-allowed opacity-50',
                  )}
                  onClick={() =>
                    isAvailable
                      ? handleDocumentSelect(documentType, !isSelected)
                      : undefined
                  }
                >
                  <Checkbox
                    isSelected={isSelected}
                    isDisabled={!isAvailable}
                    className="pointer-events-none"
                  >
                    {displayName}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 border-t border-neutral/10 p-4">
          <PrintingActionButtons
            selectedCount={selectedCount}
            onPrint={handlePrintDocuments}
            onDownload={handleDownloadDocuments}
            onSendEmail={handleSendEmail}
            onPostPrint={handlePostPrint}
            isPrinting={isPrinting}
            isDownloading={isDownloading}
            isEmailSending={isPending}
          />
        </div>
      </ContentCard>
    </div>
  );
};
